import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import { LeasingHttpService } from '@core/services/http';
import { useLeasingTabPrefetch } from '@core/utils/hooks';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { TabComponentProps } from '@models/interfaces';
import { UserPermissionKeys } from '@models/types';
import { EditablePage } from '@shared/components';
import { LeaseAudit } from '@shared/components/lease-audit/components';
import { LeasingActions } from '@store/actions';
import {
  DDVSelectors,
  LeasingSelectors,
  ProjectSelectors,
  UserSelectors,
} from '@store/selectors';

export const LeasingLeaseAuditTab: FC<
  TabComponentProps & { area: UserPermissionKeys; subPage: SubPage }
> = ({
  isActive,
  projectTypeSelector,
  area,
  subPage,
}: PropsWithChildren<
  TabComponentProps & { area: UserPermissionKeys; subPage: SubPage }
>) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();

  const isLoading = useSelector(
    LeasingSelectors.getTabLoadingState('leaseAudit'),
  );
  const isDataLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('leaseAudit'),
  );

  const { browse } = useSelector(UserSelectors.getLeasingPermissions);
  const projectType = useSelector(projectTypeSelector);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(area, subPage, Tab.LeaseAudit),
  );
  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useLeasingTabPrefetch('leaseAudit', projectBundleID);

  useEffect(() => {
    if (!isDataLoaded) {
      dispatch(
        LeasingActions.setTabLoading.done({
          tabName: 'leaseAudit',
          isLoading: true,
        }),
      );
      HttpService.getHttpRequests(LeasingHttpService).getLeasingLeaseAuditData(
        projectBundleID,
      );
    }
  }, [isDataLoaded]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-leasing-lease-audit loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <EditablePage
      isActive={isActive}
      editableComponent={LeaseAudit}
      permissions={browse}
      area={area}
      canUserEdit={
        canEdit &&
        projectTypePermission &&
        canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
    />
  );
};
